import { Order, SavedAddress } from "./type";
import { DraftOrderResponse } from "./mockApi";

type DraftItemPayload = {
  type: string;
  gas_type: string;
  price_id: number;
  number: number;
  unit_price: number;
  gas_kilogram: number;
  delivery_id: number;
};

// modify_items：API 不接受 refund_gas_type，只接受 type, price_id, number, unit_price, gas_type, gas_kilogram
type DraftItemModifyPayload = {
  type: string;
  price_id: number;
  number?: number;
  unit_price?: number;
  gas_type?: string;
  gas_kilogram?: number;
};

type DraftItemRemovePayload = {
  type: string;
  price_id: number;
};

export type DraftUpdatePayload = {
  customer_infos?: {
    tax_id_number?: string;
    carrier_type?: string;
    invoice_carrier?: string;
  };
  address_infos?: {
    address_id: number;
    customer_address_id?: number;
    address: string;
    latitude?: number;
    longitude?: number;
  };
  order_infos?: {
    delivery_type_enum: string;
    delivery_date?: string | null;
    slot_id?: number | null;
    slot_source?: "weekly" | "special" | null;
    note?: string;
  };
  add_items?: DraftItemPayload[];
  modify_items?: DraftItemModifyPayload[];
  remove_items?: DraftItemRemovePayload[];
};

const deliveryTypeEnumMap: Record<Order["deliveryType"], string> = {
  scheduled: "預約配送",
  immediate: "立即配送",
};

const toNumberOr = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// 清理對象中的 undefined 值
const cleanUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  }
  return cleaned;
};

export const buildDraftUpdatePayload = (params: {
  order: Order;
  savedAddresses: SavedAddress[];
  draftData: DraftOrderResponse | null;
}): DraftUpdatePayload => {
  const { order, savedAddresses, draftData } = params;
  const baseItems = draftData?.item_list ?? [];
  const currentItems = order.items ?? [];

  const address = savedAddresses.find((addr) => addr.id === order.addressId);
  const customerAddressIdNum = address ? parseInt(address.id, 10) : undefined;
  const draftOriginalCustomerAddressId = draftData?.address_infos?.customer_address_id;
  // address_id 優先使用編輯地址後 API 回傳的 addressBindingResult.address_id；若地址已變更但無新 address_id 則用 customer_address_id 避免送出已軟刪除的舊 binding
  const addressIdNum =
    draftOriginalCustomerAddressId != null &&
    customerAddressIdNum != null &&
    Number(draftOriginalCustomerAddressId) !== customerAddressIdNum &&
    address?.address_id == null
      ? customerAddressIdNum
      : (address?.address_id ?? customerAddressIdNum);

  // 使用 (type + price_id) 作為唯一識別 key 進行 diff，避免 index 偏移造成錯誤配對
  const baseItemsMap = new Map<string, typeof baseItems[0]>();
  baseItems.forEach((item) => {
    const key = `${item.type || "gas"}_${item.price_id}`;
    baseItemsMap.set(key, item);
  });

  const currentItemKeys = new Set<string>();

  const modifyItems: DraftItemModifyPayload[] = [];
  const addItems: DraftItemPayload[] = [];

  currentItems.forEach((currentItem) => {
    if (!currentItem.priceId) return; // 無 priceId 的項目無法識別，跳過
    const itemType = currentItem.isCisGas ? "cis_gas" : "gas";
    const key = `${itemType}_${currentItem.priceId}`;
    currentItemKeys.add(key);

    const baseItem = baseItemsMap.get(key);
    if (baseItem) {
      // 已存在的項目 — 數量有變動才加入 modify_items
      if (currentItem.quantity !== baseItem.number) {
        const modItem: DraftItemModifyPayload = {
          type: baseItem.type || "gas",
          price_id: baseItem.price_id,
          number: currentItem.quantity,
          unit_price: baseItem.unit_price,
          gas_type: baseItem.gas_type,
        };
        if (baseItem.gas_kilogram != null) {
          modItem.gas_kilogram = baseItem.gas_kilogram;
        }
        modifyItems.push(modItem);
      }
    } else {
      // 不在 baseItems 中 → 全新項目
      const unitPrice = currentItem.unitPrice || 0;
      const gasKilogram = currentItem.gasKilogram || toNumberOr(currentItem.weight, 20);
      const gasType = currentItem.gasType || (itemType === "gas" ? "液化氣" : "丙烷");
      addItems.push({
        type: itemType,
        gas_type: gasType,
        price_id: currentItem.priceId,
        number: currentItem.quantity,
        unit_price: unitPrice,
        gas_kilogram: Number(gasKilogram),
        delivery_id: 1,
      });
    }
  });

  // 在 baseItems 中存在但 currentItems 中已無對應 key → 刪除
  const removeItems: DraftItemRemovePayload[] = [];
  baseItems.forEach((baseItem) => {
    const key = `${baseItem.type || "gas"}_${baseItem.price_id}`;
    if (!currentItemKeys.has(key)) {
      removeItems.push({
        type: baseItem.type || "gas",
        price_id: baseItem.price_id,
      });
    }
  });

  const payload: any = {};

  // 只有當有項目時才添加
  if (addItems.length > 0) {
    payload.add_items = addItems;
  }
  if (modifyItems.length > 0) {
    payload.modify_items = modifyItems;
  }
  if (removeItems.length > 0) {
    payload.remove_items = removeItems;
  }

  // 只有當有值時才添加 customer_infos
  if (order.taxId || order.carrier || draftData?.customer_infos?.carrier_type) {
    const customerInfos: any = {};
    const taxId = order.taxId || draftData?.customer_infos?.tax_id_number;
    const carrier = order.carrier || draftData?.customer_infos?.invoice_carrier;
    const carrierType = draftData?.customer_infos?.carrier_type;
    
    if (taxId) customerInfos.tax_id_number = taxId;
    if (carrier) customerInfos.invoice_carrier = carrier;
    if (carrierType) customerInfos.carrier_type = carrierType;
    
    if (Object.keys(customerInfos).length > 0) {
      payload.customer_infos = customerInfos;
    }
  }

  // 只有當有地址時才添加 address_infos；address_id 與 customer_address_id 分開（草稿有 address_id 時用該值，否則 fallback）
  if (address && customerAddressIdNum != null) {
    payload.address_infos = {
      address_id: addressIdNum ?? customerAddressIdNum,
      customer_address_id: customerAddressIdNum,
      address: address.address,
      latitude: Number(address.lat),
      longitude: Number(address.lng),
    };
  }

  // 處理 order_infos
  if (order.deliveryType === "immediate") {
    // 立即配送：delivery_date、slot_id、slot_source 都設定為 null
    const orderInfos: any = {
      delivery_type_enum: deliveryTypeEnumMap[order.deliveryType],
      delivery_date: null,
      slot_id: null,
      slot_source: null,
    };
    
    // 添加訂單備注（如果有的話）
    if (order.note) {
      orderInfos.note = order.note;
    }
    
    payload.order_infos = orderInfos;
  } else if (order.deliveryType === "scheduled") {
    // 預約配送：delivery_date、slot_id、slot_source 不能為空
    const orderInfos: any = {
      delivery_type_enum: deliveryTypeEnumMap[order.deliveryType],
    };

    // 必須有 delivery_date
    if (!order.deliveryDate) {
      throw new Error("預約配送必須選擇配送日期");
    }
    orderInfos.delivery_date = order.deliveryDate;

    // 驗證：預約配送時，slot_id 和 slot_source 必須有值
    if (!order.deliverySlotId || !order.deliverySlotSource) {
      throw new Error("預約配送必須選擇配送時段");
    }
    orderInfos.slot_id = order.deliverySlotId;
    orderInfos.slot_source = order.deliverySlotSource;

    // 添加訂單備注
    if (order.note) {
      orderInfos.note = order.note;
    }

    payload.order_infos = orderInfos;
  }

  // 清理所有 undefined 值
  return cleanUndefined(payload) as DraftUpdatePayload;
};
